import React from 'react';

import {
	type DevDocsBylineConnectionAncestor,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import {
	BylineConnectionsPlaceholderVisual,
	type BylineConnectionsPlaceholderVisualProps,
} from '../BylineConnectionsPlaceholderVisual.tsx';
import type { BylineContentType } from '../utils/contentAri.ts';

import { shouldPaintBylineSsrPlaceholder } from './shouldPaintBylineSsrPlaceholder.ts';
import { useIncomingLinksSsrState } from './useIncomingLinksSsrState.ts';
import { useOutboundBylineSsrState } from './useOutboundBylineSsrState.ts';
import { useRelatedContentSsrState } from './useRelatedContentSsrState.ts';

export type BylineConnectionsPlaceholderProps = Pick<
	BylineConnectionsPlaceholderVisualProps,
	'isOpen' | 'onOpenChange'
> & {
	contentId?: string;
	/** Keys the graph-backed SSR sample reads on the content's ARI. Undefined means page. */
	contentType?: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
};

export const BylineConnectionsPlaceholder = ({
	contentId,
	contentType,
	connectionAncestors,
	isOpen,
	onOpenChange,
}: BylineConnectionsPlaceholderProps): React.JSX.Element | null => {
	// Read the preloaded states on the client too (store-only, hydrated from the SSR preloads). This
	// lets the placeholder stay visible after the post-paint hydration and while the LoadableAfterPaint
	// chunk loads, instead of blanking — which caused the "SSR placeholder → nothing → skeleton → item"
	// flash. The result is derived purely from the hydrated store and dev-docs, so it is identical on
	// the server and the client's first render (no hydration mismatch). CCINT-8752
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingLinksState = useIncomingLinksSsrState(contentId, contentType);
	const relatedContentState = useRelatedContentSsrState(contentId, contentType);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);

	// Paint only when the sampled sources together reach the byline's row minimum (or Dev Docs), the
	// same rule the loaded byline applies, so a page that will hide keeps no footprint. CCINT-9359
	const shouldPaint = shouldPaintBylineSsrPlaceholder({
		outboundState,
		incomingLinksState,
		relatedContentState,
		hasDevDocsConnection: devDocsConnection !== null,
	});

	if (!shouldPaint) {
		return null;
	}

	// The footprint is fixed at three tiles for any positive signal. CCINT-9354
	return <BylineConnectionsPlaceholderVisual isOpen={isOpen} onOpenChange={onOpenChange} />;
};
